/**
 * Estimate how long the user will take to complete a story given a
 * recording cadence. Used by the FrequencyPicker so users see the
 * trade-off between cadence and time-to-completion at decision time.
 *
 * Inputs:
 *  - daysPerQuestion: average days between recordings (e.g., 1 for daily,
 *    2.5 for "every 2-3 days", 7 for weekly).
 *  - totalQuestions: number of topics in the story (currently 42 from the
 *    master template — see TOTAL_STORY_TOPICS in constants.ts).
 *
 * Output is a short Spain-Spanish phrase ("6 semanas", "3-4 meses",
 * "10 meses", "~1 año"). Designed to fit inside a chip below the cadence
 * label.
 */

export interface EstimateInput {
    daysPerQuestion: number;
    totalQuestions: number;
}

/**
 * Format a completion estimate as a short Spanish phrase.
 *
 * Special case: when daysPerQuestion is 2.5 (the "every 2-3 days" cadence),
 * we return a range that reflects the underlying variance, e.g. "3-4 meses".
 */
export function formatCompletionEstimate({
    daysPerQuestion,
    totalQuestions,
}: EstimateInput): string {
    if (daysPerQuestion === 2.5) {
        const lowerDays = 2 * totalQuestions;
        const upperDays = 3 * totalQuestions;
        const lowerMonths = Math.round(lowerDays / 30);
        const upperMonths = Math.round(upperDays / 30);
        if (lowerMonths === upperMonths) {
            return formatSingle(Math.round(daysPerQuestion * totalQuestions));
        }
        return `${lowerMonths}-${upperMonths} meses`;
    }

    return formatSingle(Math.round(daysPerQuestion * totalQuestions));
}

function formatSingle(days: number): string {
    if (days <= 14) return `${days} días`;

    const weeks = Math.round(days / 7);
    if (weeks <= 8) return `${weeks} semanas`;

    const months = Math.round(days / 30);
    if (months === 1) return '1 mes';
    if (months <= 11) return `${months} meses`;

    return '~1 año';
}
